/**
 * RegExp to match header token field-content: https://datatracker.ietf.org/doc/html/rfc7230#section-3.2
 */

const fieldContentRegExp = /^[\u0009\u0020-\u007e\u0080-\u00ff]+$/;
const __toString = Object.prototype.toString;

export function hasDocumentCookie() {
    return typeof document === 'object' && typeof document.cookie === 'string';
}

export function cleanCookies() {
    document.cookie.split(';').forEach(c => {
        document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
    });
}

export function isInBrowser() {
    return (
        typeof window !== 'undefined' &&
        typeof window.document !== 'undefined' &&
        typeof window.document.createElement !== 'undefined'
    );
}

export function parseCookies(cookies, options) {
    if (typeof cookies === 'string') {
        return parse(cookies, options);
    } else if (typeof cookies === 'object' && cookies !== null) {
        return cookies;
    } else {
        return {};
    }
}

export function isParsingCookie(value, doNotParse) {
    if (typeof doNotParse === 'undefined') {
        // We guess if the cookie start with { or [, it has been serialized
        doNotParse = !value || (value[0] !== '{' && value[0] !== '[' && value[0] !== '"');
    }

    return !doNotParse;
}

export function readCookie(value, options) {
    const cleanValue = cleanupCookieValue(value);
    if (isParsingCookie(cleanValue, options?.doNotParse)) {
        try {
            return JSON.parse(cleanValue);
        } catch (e) {
            //We tried
        }
    }

    // Ignore clean value if we failed the deserialization
    // It is not relevant anymore to trim those values
    return value;
}

function cleanupCookieValue(value) {
    // express prepend j: before serializing a cookie
    if (value && value[0] === 'j' && value[1] === ':') {
        return value.substr(2);
    }

    return value;
}

/**
 * Parses a cookie header.
 *
 * Parse the given cookie header string into an object
 * The object has the various cookies as keys(names) => values
 *
 * @param {string} str
 * @param {object} [options]
 * @return {object}
 */
export function parse(str, options) {
    if (typeof str !== 'string') {
        throw new TypeError('argument str must be a string');
    }

    let obj = {};
    const opt = options || {};
    const dec = opt.decode || decode;

    let index = 0;
    while (index < str.length) {
        var eqIdx = str.indexOf('=', index);

        if (eqIdx === -1) {
            break;
        }

        let endIdx = str.indexOf(';', index);

        if (endIdx === -1) {
            endIdx = str.length;
        } else if (endIdx < eqIdx) {
            index = str.lastIndexOf(';', eqIdx - 1) + 1;
            continue;
        }

        const key = str.slice(index, eqIdx).trim();

        if (undefined === obj[key]) {
            var val = str.slice(eqIdx + 1, endIdx).trim();

            if (val.charCodeAt(0) === 0x22) {
                val = val.slice(1, -1);
            }

            obj[key] = tryDecode(val, dec);
        }

        index = endIdx + 1;
    }

    return obj;
}

/**
 * URL-decode string value. Optimized to skip native call when no %.
 *
 * @param {string} str
 * @returns {string}
 */
function decode(str) {
    return str.indexOf('%') !== -1 ? decodeURIComponent(str) : str;
}

/**
 * URL-encode value.
 *
 * @param {string} val
 * @returns {string}
 */
function encode(val) {
    return encodeURIComponent(val);
}

/**
 * Serialize data into a cookie header.
 *
 * Serialize the a name value pair into a cookie string suitable for
 * http headers. An optional options object specified cookie parameters.
 *
 * serialize('foo', 'bar', { httpOnly: true })
 *   => "foo=bar; httpOnly"
 *
 * @param {string} name
 * @param {string} val
 * @param {object} [options]
 * @return {string}
 */

export function serialize(name, val, options) {
    const opt = options || {};
    const enc = opt.encode || encode;

    if (typeof enc !== 'function') {
        throw new TypeError('option encode is invalid');
    }

    if (!fieldContentRegExp.test(name)) {
        throw new TypeError('argument name is invalid');
    }

    const value = enc(val);

    if (value && !fieldContentRegExp.test(value)) {
        throw new TypeError('argument val is invalud');
    }

    let result = name + '=' + value;

    if (opt.maxAge != null) {
        var maxAge = opt.maxAge - 0;

        if (isNaN(maxAge) || !isFinite(maxAge)) {
            throw new TypeError('option maxAge is invalid');
        }

        result += '; Max-Age=' + Math.floor(maxAge);
    }

    if (opt.domain) {
        if (!fieldContentRegExp.test(opt.domain)) {
            throw new TypeError('option domain is invalid');
        }

        result += '; Domain=' + opt.domain;
    }

    if (opt.path) {
        if (!fieldContentRegExp.test(opt.path)) {
            throw new TypeError('option path is invalid');
        }

        result += '; Path=' + opt.path;
    }

    if (opt.expires) {
        const expires = opt.expires;

        if (!isDate(expires) || isNaN(expires.valueOf())) {
            throw new TypeError('option expires is invalid');
        }

        result += '; Expires=' + expires.toUTCString();
    }

    if (opt.httpOnly) {
        result += '; HttpOnly';
    }

    if (opt.secure) {
        result += '; Secure';
    }

    if (opt.priority) {
        const priority = typeof opt.priority === 'string' ? opt.priority.toLowerCase() : opt.priority;

        switch (priority) {
            case 'low':
                result += '; Priority=Low';
                break;
            case 'medium':
                result += '; Priority=Medium';
                break;
            case 'high':
                result += '; Priority=High';
                break;
            default:
                throw new TypeError('option priority invalid');
        }
    }

    if (opt.sameSite) {
        const sameSite = typeof opt.sameSite === 'string' ? opt.sameSite.toLowerCase() : opt.sameSite;

        switch (sameSite) {
            case true:
                result += '; SameSite=Strict';
                break;
            case 'lax':
                result += '; SameSite=Lax';
                break;
            case 'strict':
                result += '; SameSite=Strict';
                break;
            case 'none':
                result += '; SameSite=None';
                break;
            default:
                throw new TypeError('option sameSite is invalid');
        }
    }

    return result;
}

/**
 * Determine if value is a Date.
 *
 * @param {*} val
 */

function isDate(val) {
    return __toString.call(val) === '[object Date]' || val instanceof Date;
}

/**
 * Try decoding a string using a decoding function.
 *
 * @param {string} str
 * @param {function} decode
 */
function tryDecode(str, decode) {
    try {
        return decode(str);
    } catch (e) {
        return str;
    }
}
